// @story HIR-HR7
// Data retention for one candidate, on the candidate panel: how long their
// data is kept, the date it would be removed, and an erase action that asks
// for the application id to be typed back before it does anything.
import React, { useEffect, useState } from 'react';
import { ShieldCheck, Trash2 } from 'lucide-react';
import api from '../../../services/api';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export default function CandidateRetention({ resumeId, onErased }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [includeRelated, setIncludeRelated] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    api.get(`/retention/candidates/${resumeId}`)
      .then((r) => setData(r.data?.data || null))
      .catch((e) => setErr(e.response?.data?.message || 'Could not load retention for this candidate.'));
  };
  useEffect(load, [resumeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmWord = data?.applicationId || 'ERASE';

  const erase = () => {
    setBusy(true); setErr('');
    api.post(`/retention/candidates/${resumeId}/erase`, {
      confirm: typed.trim(),
      includeRelated,
      requestId: data?.request?.id || undefined,
    })
      .then((r) => {
        setResult(r.data?.data || null);
        if (r.data?.data?.complete && onErased) onErased();
      })
      .catch((e) => setErr(e.response?.data?.message || 'Could not erase this candidate.'))
      .finally(() => setBusy(false));
  };

  if (err && !data) return <div className="mb-6 pt-4 border-t text-sm text-red-600">{err}</div>;
  if (!data) return null;

  let line;
  if (data.state === 'hired') {
    line = 'Hired — this record hands over to People and is not removed by the retention clock.';
  } else if (data.state === 'unsuccessful') {
    const when = fmt(data.removeAfter || data.expectedRemoveAfter);
    line = data.autoDelete
      ? `Will be removed on ${when} (${data.months} months after the outcome).`
      : `Would be removed on ${when} (${data.months} months after the outcome). Automatic removal is off, so nothing is deleted on that date yet.`;
  } else if (data.state === 'general_pool') {
    const when = fmt(data.removeAfter || data.expectedRemoveAfter);
    line = data.autoDelete
      ? `In the General applications pool. Will be removed on ${when} (${data.months} months after the last activity on this application). New activity moves the date on; moved onto a role, they follow that role's rule.`
      : `In the General applications pool. Would be removed on ${when} (${data.months} months after the last activity on this application). New activity moves the date on; moved onto a role, they follow that role's rule. Automatic removal is off, so nothing is deleted on that date yet.`;
  } else {
    line = `Still in the running. If unsuccessful, kept ${data.months} months after the outcome, then removed.`;
  }

  return (
    <div className="mb-6 pt-4 border-t">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-gray-900 flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-green-700" /> Data retention
        </h4>
        {!open && !result && data.state !== 'hired' && (
          <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700">
            <Trash2 className="w-3 h-3" /> Erase data
          </button>
        )}
      </div>
      <p className="text-sm text-gray-700">{line}</p>
      {data.reason && data.state === 'unsuccessful' && <p className="text-xs text-gray-500 mt-1">Outcome: {data.reason}</p>}
      {data.request && (
        <p className="text-xs mt-2 px-2 py-1.5 rounded bg-orange-50 text-orange-800 border border-orange-100">
          The candidate asked for their data to be removed on {fmt(data.request.requestedAt)}. Erasing here completes that request.
        </p>
      )}
      {data.erasurePending && (
        <p className="text-xs mt-2 px-2 py-1.5 rounded bg-orange-50 text-orange-800 border border-orange-100">
          Erasure started — still to clear: {data.erasurePending.join(', ')}. Retried automatically.
        </p>
      )}

      {open && !result && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm font-medium text-red-800">This cannot be undone.</p>
          <p className="text-xs text-red-800 mt-1">Erasing removes this candidate from:</p>
          <ul className="text-xs text-red-800 list-disc ml-5 mt-1">
            {(data.willClear || []).map((s) => <li key={s}>{s}</li>)}
          </ul>
          <p className="text-xs text-red-800 mt-2">A record that the erasure happened, and who did it, is kept — without the person's details.</p>
          {data.related?.length > 0 && (
            <label className="flex items-start gap-2 mt-2 text-xs text-red-900">
              <input type="checkbox" checked={includeRelated} onChange={(e) => setIncludeRelated(e.target.checked)} className="mt-0.5" />
              <span>
                Also erase {data.related.length} other application{data.related.length > 1 ? 's' : ''} that look like the same person
                ({data.related.map((x) => x.applicationId || x.name).filter(Boolean).join(', ')}).
                {data.related.some((x) => x.hired) ? ' Hired records among them are left alone.' : ''}
              </span>
            </label>
          )}
          <label className="block text-xs text-red-900 mt-3 mb-1">Type <strong>{confirmWord}</strong> to confirm</label>
          <input value={typed} onChange={(e) => setTyped(e.target.value)}
            className="w-full border border-red-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-red-400" />
          {err && <p className="text-xs text-red-700 mt-2">{err}</p>}
          <div className="flex gap-2 mt-3">
            <button onClick={erase} disabled={busy || typed.trim().toUpperCase() !== confirmWord.toUpperCase()}
              className="px-3 py-1.5 text-xs font-medium rounded bg-red-600 text-white disabled:opacity-50">
              {busy ? 'Erasing…' : 'Erase permanently'}
            </button>
            <button onClick={() => { setOpen(false); setTyped(''); setErr(''); }} className="px-3 py-1.5 text-xs text-gray-600">Cancel</button>
          </div>
        </div>
      )}

      {result && (
        <div className={`mt-3 rounded-lg border p-3 text-xs ${result.complete ? 'border-green-200 bg-green-50 text-green-900' : 'border-orange-200 bg-orange-50 text-orange-900'}`}>
          <p className="font-medium mb-1">{result.complete ? 'Erased everywhere.' : 'Erasure started — some places are still to clear and will be retried automatically.'}</p>
          {(result.results || []).map((r) => (
            <div key={r.id} className="mt-1">
              <div className="font-medium">{r.applicationId || 'Record'}</div>
              <ul className="list-disc ml-5">
                {r.steps.map((s, i) => (
                  <li key={`${s.store}-${i}`}>{s.store}: {{
                    removed: `removed${s.count ? ` (${s.count})` : ''}`,
                    nothing_held: 'nothing held',
                    failed: `not yet — ${s.note}`,
                    skipped: s.note || 'skipped',
                  }[s.status]}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
